import { InputNumber, Table } from 'antd';
import Column from 'antd/es/table/Column';
import React from 'react';
import { currencyFormat, layTenNhomNhanSu } from '../../helpers/common';
import { ChiTietLuongCoBanItem, DynamicColumnHeader } from '../../models/luong/LuongCoBanModel';

const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

export interface FlatRowChiTietLuongCoBan {
    key: string;
    type: 'grand-summary' | 'group-summary' | 'employee';
    groupIndex?: string;
    employeeIndex?: number;
    originalIndex: number;
    id?: number;
    idLuongCoBan?: number;
    idNhanVien?: number;
    idChucVu?: number;
    idBacLuong?: number;
    heSo?: number;
    idHeSoLuong?: number;
    idKPI?: number;
    thangNam?: Date;
    congThang?: number;
    soCong?: number;
    mucLuongHopDong?: number;
    tienLuongTamUng?: number;
    tongGiamTru?: number;
    congDoan?: number;
    luongKhac?: number;
    xepLoai?: number;
    luongDuocNhan?: number;
    luongDaNhan?: number;
    luongCBThucNhan?: number;
    ma?: string;
    hoTen: string | React.ReactNode;
    tenChucVu?: string;
    tenDonVi?: string;
    bac?: string;
    idNhomNhanSu?: number;
    isQuanLy?: boolean;
    dynamicColumns?: { [key: string]: number };
}

const TongLuongCoBan = (
    data: ChiTietLuongCoBanItem[],
    deductionCols: DynamicColumnHeader[],
    additionCols: DynamicColumnHeader[]
) => {
    // Với cột mức lương đóng BHXH/HĐLĐ: nếu cùng 1 idNhanVien có nhiều row thì lấy số tiền lớn hơn
    const maxMucLuongByNhanVien = new Map<number, number>();
    let unnamedMucLuong = 0;

    data.forEach(item => {
        const id = item.idNhanVien;
        const val = item.mucLuongHopDong || 0;
        if (id) {
            const currentMax = maxMucLuongByNhanVien.get(id) ?? 0;
            if (val > currentMax) {
                maxMucLuongByNhanVien.set(id, val);
            }
        } else {
            unnamedMucLuong += val;
        }
    });

    let tongMucLuongHopDong = unnamedMucLuong;
    maxMucLuongByNhanVien.forEach(val => {
        tongMucLuongHopDong += val;
    });

    const sums = {
        mucLuongHopDong: tongMucLuongHopDong,
        tienLuongTamUng: 0,
        tongGiamTru: 0,
        congDoan: 0,
        luongKhac: 0,
        luongDuocNhan: 0,
        luongDaNhan: 0,
        luongCBThucNhan: 0,
        dynamicTotals: {} as { [key: string]: number }
    };

    deductionCols.forEach(col => { sums.dynamicTotals[`deduction_${col.id}`] = 0; });
    additionCols.forEach(col => { sums.dynamicTotals[`addition_${col.id}`] = 0; });

    // Các cột còn lại tính tổng bình thường trên tất cả các row
    data.forEach(item => {
        sums.tienLuongTamUng += item.tienLuongTamUng || 0;
        sums.tongGiamTru += item.tongGiamTru || 0;
        sums.congDoan += item.congDoan || 0;
        sums.luongKhac += item.luongKhac || 0;
        sums.luongDuocNhan += item.luongDuocNhan || 0;
        sums.luongDaNhan += item.luongDaNhan || 0;
        sums.luongCBThucNhan += item.luongCBThucNhan || 0;

        deductionCols.forEach(col => {
            if (col.id === -1) {
                sums.dynamicTotals[`deduction_${col.id}`] += item.congDoan || 0;
            } else {
                sums.dynamicTotals[`deduction_${col.id}`] += item.dynamicColumns?.[`deduction_${col.id}`] || 0;
            }
        });
        additionCols.forEach(col => {
            sums.dynamicTotals[`addition_${col.id}`] += item.dynamicColumns?.[`addition_${col.id}`] || 0;
        });
    });

    return sums;
};

const buildFlatRows = (
    items: ChiTietLuongCoBanItem[],
    dynamicColumns?: DynamicColumnHeader[],
    isDieuChinh?: boolean
): FlatRowChiTietLuongCoBan[] => {
    const flatRows: FlatRowChiTietLuongCoBan[] = [];
    if (!items || items.length === 0) return flatRows;

    const deductionColumns = (dynamicColumns || []).filter(x => x.loai === 0);
    const additionColumns = (dynamicColumns || []).filter(x => x.loai === 1);

    // Group items by idNhomNhanSu (fallback: item.idNhom || (item.isQuanLy ? 3 : 1))
    const groups: { [key: number]: { originalIndex: number; item: ChiTietLuongCoBanItem }[] } = {};
    items.forEach((item, index) => {
        const nhomId = item.idNhomNhanSu || (item as any).idNhom || (item.isQuanLy ? 3 : 1);
        if (!groups[nhomId]) {
            groups[nhomId] = [];
        }
        groups[nhomId].push({ originalIndex: index, item });
    });

    const preferredOrder = [3, 7, 1];
    const allGroupIds = Object.keys(groups).map(Number);
    const sortedGroupIds: number[] = [
        ...preferredOrder.filter(id => allGroupIds.includes(id)),
        ...allGroupIds.filter(id => !preferredOrder.includes(id))
    ];

    const grandTotals = TongLuongCoBan(items, deductionColumns, additionColumns);

    // 1. Grand Summary Row at top
    flatRows.push({
        key: 'grand-summary',
        type: 'grand-summary',
        originalIndex: -1,
        hoTen: 'Tổng cộng',
        mucLuongHopDong: grandTotals.mucLuongHopDong,
        tienLuongTamUng: grandTotals.tienLuongTamUng,
        tongGiamTru: grandTotals.tongGiamTru,
        congDoan: grandTotals.congDoan,
        luongKhac: grandTotals.luongKhac,
        luongDuocNhan: grandTotals.luongDuocNhan,
        luongDaNhan: grandTotals.luongDaNhan,
        luongCBThucNhan: grandTotals.luongCBThucNhan,
        dynamicColumns: grandTotals.dynamicTotals,
    });

    let employeeSTT = 1;

    sortedGroupIds.forEach((nhomId, groupIdx) => {
        const groupItems = groups[nhomId];
        const groupName = nhomId === 7 ? "Trưởng Ban kiểm soát" : layTenNhomNhanSu(nhomId);
        const groupRoman = romanNumerals[groupIdx] || (groupIdx + 1).toString();

        const rawGroupItems = groupItems.map(g => g.item);
        const groupTotals = TongLuongCoBan(rawGroupItems, deductionColumns, additionColumns);

        // 2. Group Summary Row
        flatRows.push({
            key: `group-summary-${nhomId}`,
            type: 'group-summary',
            originalIndex: -1,
            groupIndex: groupRoman,
            hoTen: groupName,
            mucLuongHopDong: groupTotals.mucLuongHopDong,
            tienLuongTamUng: groupTotals.tienLuongTamUng,
            tongGiamTru: groupTotals.tongGiamTru,
            congDoan: groupTotals.congDoan,
            luongKhac: groupTotals.luongKhac,
            luongDuocNhan: groupTotals.luongDuocNhan,
            luongDaNhan: groupTotals.luongDaNhan,
            luongCBThucNhan: groupTotals.luongCBThucNhan,
            dynamicColumns: groupTotals.dynamicTotals,
        });

        // 3. Group Employee Rows
        groupItems.forEach(({ originalIndex, item }) => {
            flatRows.push({
                key: `employee-${item.idNhanVien || item.id}-${originalIndex}`,
                type: 'employee',
                employeeIndex: employeeSTT++,
                originalIndex,
                id: item.id,
                idLuongCoBan: item.idLuongCoBan,
                idNhanVien: item.idNhanVien,
                idChucVu: item.idChucVu,
                idBacLuong: item.idBacLuong,
                heSo: item.heSo,
                idHeSoLuong: item.idHeSoLuong,
                idKPI: item.idKPI,
                thangNam: item.thangNam,
                congThang: item.congThang,
                soCong: item.soCong,
                mucLuongHopDong: item.mucLuongHopDong,
                tienLuongTamUng: item.tienLuongTamUng,
                tongGiamTru: item.tongGiamTru,
                congDoan: item.congDoan,
                luongKhac: item.luongKhac,
                xepLoai: item.xepLoai,
                luongDuocNhan: item.luongDuocNhan,
                luongDaNhan: item.luongDaNhan,
                luongCBThucNhan: item.luongCBThucNhan,
                ma: item.ma,
                hoTen: item.hoTen,
                tenChucVu: item.tenChucVu,
                tenDonVi: item.tenDonVi,
                bac: item.bac,
                idNhomNhanSu: item.idNhomNhanSu,
                isQuanLy: item.isQuanLy,
                dynamicColumns: item.dynamicColumns,
            });
        });
    });

    return flatRows;
};

const TableChiTietLuongCoBan = ({
    loading,
    dataSource,
    dynamicColumns,
    isDieuChinh,
    isEdit,
    onChangeItem,
}: {
    loading: boolean;
    dataSource: ChiTietLuongCoBanItem[];
    dynamicColumns?: DynamicColumnHeader[];
    isDieuChinh?: boolean;
    isEdit?: boolean;
    onChangeItem?: (index: number, field: string, value: number) => void;
}) => {
    // Group dynamic columns by Loai (0 = Giảm trừ, 1 = Tăng thêm)
    const deductionColumns = (dynamicColumns || []).filter(x => x.loai === 0);
    const additionColumns = (dynamicColumns || []).filter(x => x.loai === 1);

    // Kiểm tra nhân viên bất kì có luongKhac > 0 -> Thêm cột thu nhập thêm
    const hasAdditionalIncome = (dataSource || []).some(x => (x.luongKhac ?? 0) > 0);

    const flatRows = buildFlatRows(dataSource, dynamicColumns, isDieuChinh);

    return (
        <>
            <style>{`
                .table-row-grand-summary td {
                    background-color: #fff9e6 !important;
                    font-weight: bold;
                }
                .table-row-group-summary td {
                    background-color: #e6f7ff !important;
                    font-weight: bold;
                }
            `}</style>
            <Table
                sticky
                pagination={false}
                scroll={{ x: Math.max(1800, 1800 + (deductionColumns.length + additionColumns.length) * 150 + (isDieuChinh ? 150 : 0)) }}
                loading={loading}
                bordered
                locale={{ emptyText: 'Không có dữ liệu' }}
                dataSource={flatRows}
                rowClassName={(record: FlatRowChiTietLuongCoBan) => {
                    if (record.type === 'grand-summary') return 'table-row-grand-summary';
                    if (record.type === 'group-summary') return 'table-row-group-summary';
                    return '';
                }}
            >
                <Column
                    fixed
                    title="STT"
                    width="60px"
                    key="stt"
                    render={(_v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary') return "";
                        if (record.type === 'group-summary') return <strong>{record.groupIndex}</strong>;
                        return record.employeeIndex;
                    }}
                />
                <Column
                    fixed
                    title="Mã NV"
                    dataIndex="ma"
                    key="ma"
                    width="100px"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    fixed
                    title="Họ tên"
                    dataIndex="hoTen"
                    key="hoTen"
                    width="180px"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{v}</strong>;
                        }
                        return v;
                    }}
                />
                <Column
                    title="Chức vụ"
                    dataIndex="tenChucVu"
                    key="chucVu"
                    width="160px"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    title="Phòng ban"
                    dataIndex="tenDonVi"
                    key="tenDonVi"
                    width="160px"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    title="Bậc lương"
                    dataIndex="bac"
                    key="bac"
                    width="120px"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    title="Hệ số"
                    dataIndex="heSo"
                    key="heSo"
                    width="90px"
                    align="right"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v != null ? v : '';
                    }}
                />
                <Column
                    title="Công tháng"
                    dataIndex="congThang"
                    key="congThang"
                    width="90px"
                    align="right"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v != null ? v : '';
                    }}
                />
                <Column
                    title={isDieuChinh ? "Số công" : "Số công tạm ứng"}
                    dataIndex="soCong"
                    key="soCong"
                    width="110px"
                    align="right"
                    render={(v: number, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        if (isEdit) {
                            return (
                                <InputNumber
                                    size="small"
                                    style={{ width: '100%' }}
                                    value={v}
                                    min={0}
                                    onBlur={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        onChangeItem?.(record.originalIndex, 'soCong', val);
                                    }}
                                />
                            );
                        }
                        return v != null ? v : '';
                    }}
                />
                <Column
                    title="Tiền lương đóng BHXH/HĐLĐ"
                    dataIndex="mucLuongHopDong"
                    key="mucLuongHopDong"
                    width="140px"
                    align="right"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                        }
                        return currencyFormat.formatMoney(v ?? 0);
                    }}
                />
                {!isDieuChinh && (
                    <Column
                        title="Tiền lương tạm ứng"
                        dataIndex="tienLuongTamUng"
                        key="tienLuongTamUng"
                        width="150px"
                        align="right"
                        render={(v: number, record: FlatRowChiTietLuongCoBan) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                            }
                            if (isEdit) {
                                return (
                                    <InputNumber
                                        size="small"
                                        style={{ width: '100%' }}
                                        value={v}
                                        min={0}
                                        formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={(val) => (val ?? '').replace(/,/g, '') as any}
                                        onBlur={(e) => {
                                            const val = parseFloat((e.target.value ?? '').replace(/,/g, '')) || 0;
                                            onChangeItem?.(record.originalIndex, 'tienLuongTamUng', val);
                                        }}
                                    />
                                );
                            }
                            return currencyFormat.formatMoney(v ?? 0);
                        }}
                    />
                )}
                <Column
                    title="Tổng các khoản giảm trừ"
                    dataIndex="tongGiamTru"
                    key="tongGiamTru"
                    width="130px"
                    align="right"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                        }
                        return currencyFormat.formatMoney(v ?? 0);
                    }}
                />

                {/* Group 1: Giảm trừ */}
                {deductionColumns.length > 0 && (
                    <Column title="Trong đó" key="trongDoGiamTru">
                        {deductionColumns.map((col) => (
                            <Column
                                key={`deduction_${col.id}`}
                                title={col.name}
                                width="130px"
                                align="right"
                                render={(_v: any, record: FlatRowChiTietLuongCoBan) => {
                                    if (col.id === -1) {
                                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                            return <strong>{currencyFormat.formatMoney(record.congDoan ?? 0)}</strong>;
                                        }
                                        if (isEdit) {
                                            return (
                                                <InputNumber
                                                    size="small"
                                                    style={{ width: '100%' }}
                                                    value={record.congDoan ?? 0}
                                                    min={0}
                                                    formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    parser={(val) => (val ?? '').replace(/,/g, '') as any}
                                                    onBlur={(e) => {
                                                        const val = parseFloat((e.target.value ?? '').replace(/,/g, '')) || 0;
                                                        onChangeItem?.(record.originalIndex, 'congDoan', val);
                                                    }}
                                                />
                                            );
                                        }
                                        return currencyFormat.formatMoney(record.congDoan ?? 0);
                                    }
                                    const value = record.dynamicColumns?.[`deduction_${col.id}`] ?? 0;
                                    if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                        return <strong>{currencyFormat.formatMoney(value)}</strong>;
                                    }
                                    return currencyFormat.formatMoney(value);
                                }}
                            />
                        ))}
                    </Column>
                )}

                {/* "Thu nhập phát sinh thêm" column */}
                {hasAdditionalIncome && (
                    <Column
                        title="Thu nhập phát sinh thêm"
                        dataIndex="luongKhac"
                        key="luongKhac"
                        width="150px"
                        align="right"
                        render={(v, record: FlatRowChiTietLuongCoBan) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                            }
                            return currencyFormat.formatMoney(v ?? 0);
                        }}
                    />
                )}

                {/* Group 2: Lương tăng thêm */}
                {additionColumns.length > 0 && (
                    <Column title="Trong đó" key="trongDoTangThem">
                        {additionColumns.map((col) => (
                            <Column
                                key={`addition_${col.id}`}
                                title={col.name}
                                width="130px"
                                align="right"
                                render={(_v, record: FlatRowChiTietLuongCoBan) => {
                                    const value = record.dynamicColumns?.[`addition_${col.id}`] ?? 0;
                                    if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                        return <strong>{currencyFormat.formatMoney(value)}</strong>;
                                    }
                                    return currencyFormat.formatMoney(value);
                                }}
                            />
                        ))}
                    </Column>
                )}

                <Column
                    title="Lương được nhận"
                    dataIndex="luongDuocNhan"
                    key="luongDuocNhan"
                    width="150px"
                    align="right"
                    render={(v: number, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                        }
                        if (isEdit) {
                            return (
                                <InputNumber
                                    size="small"
                                    style={{ width: '100%' }}
                                    value={v}
                                    formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(val) => (val ?? '').replace(/,/g, '') as any}
                                    onBlur={(e) => {
                                        const val = parseFloat((e.target.value ?? '').replace(/,/g, '')) || 0;
                                        onChangeItem?.(record.originalIndex, 'luongDuocNhan', val);
                                    }}
                                />
                            );
                        }
                        return currencyFormat.formatMoney(v ?? 0);
                    }}
                />
                {isDieuChinh && (
                    <Column
                        title="Tiền lương đã tạm ứng"
                        dataIndex="luongDaNhan"
                        key="luongDaNhan"
                        width="150px"
                        align="right"
                        render={(v, record: FlatRowChiTietLuongCoBan) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                            }
                            return currencyFormat.formatMoney(v ?? 0);
                        }}
                    />
                )}
                <Column
                    title="Số tiền lương còn được nhận"
                    dataIndex="luongCBThucNhan"
                    key="luongCBThucNhan"
                    width="170px"
                    align="right"
                    render={(v, record: FlatRowChiTietLuongCoBan) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(v ?? 0)}</strong>;
                        }
                        return currencyFormat.formatMoney(v ?? 0);
                    }}
                />
            </Table>
        </>
    );
};

export default TableChiTietLuongCoBan;